import { Controller } from "@hotwired/stimulus"
import { persistSidebarMode } from "../utils/sidebar_mode"
import { COLLAPSED_CLASS, FOOTER_HIDDEN_CLASS } from "../lib/sidebar_collapse_state"

// Toggles which sidebar partial is visible (main vs settings) without navigating.
// CSS in goatleads/components/_sidebar.scss reads data-sidebar-mode-active-value
// off the body element to hide the inactive sidebar. The choice is persisted to a
// cookie so it survives full page loads / refreshes — the server reads it back in
// _layout.html.haml, so the nav bar is not derived from the URL (#6074).
// Handoff with the React datatable shell (useSettingsRailHandoff.ts mirrors
// these names verbatim — the two bundles cannot share a module). Before the
// gear opens the settings nav it fires BEFORE_OPEN_SETTINGS_EVENT (cancelable)
// on document; a page that must make room first — a full-page table whose
// right panel is open, where _panels.scss hides the settings rail outright
// (#11886), so a bare swap would look like the gear did nothing (#12314) — claims
// it with preventDefault, closes its panel, then fires OPEN_SETTINGS_EVENT to
// finish the swap. If it never does (a close guard vetoed), nothing changes.
export const BEFORE_OPEN_SETTINGS_EVENT = "sidebar-mode:before-open-settings"
export const OPEN_SETTINGS_EVENT = "sidebar-mode:open-settings"

export default class extends Controller {
  static values = {
    active: { type: String, default: "main" },
    mainUrl: { type: String, default: "/" },
  }

  connect() {
    this._onOpenSettingsRequest = () => this.openSettings()
    document.addEventListener(OPEN_SETTINGS_EVENT, this._onOpenSettingsRequest)
  }

  disconnect() {
    document.removeEventListener(OPEN_SETTINGS_EVENT, this._onOpenSettingsRequest)
  }

  // On a SETTINGS SURFACE (body.gl-settings-context) the settings rail is
  // route-derived by the server — it is the screen's only navigation — so
  // "closing" it cannot be a bare
  // nav-bar swap: that would strand the page with no settings navigation and a
  // refresh would bring the rail straight back. Closing there means LEAVING
  // settings, i.e. navigating back to the app.
  onSettingsSurface() {
    return document.body.classList.contains("gl-settings-context")
  }

  // The ⚙ Settings button is a toggle: it opens the settings nav, and if the
  // settings nav is already open it closes it again (same swap the Close pill
  // does). Neither direction navigates — EXCEPT closing while on a settings
  // surface, which leaves settings entirely (see onSettingsSurface).
  //
  // The close branch is reachable on desktop: _sidebar.scss hides
  // .gl-sidebar--mode-main in settings mode (0,2,1), but the Wave 1 settings
  // shell (#10914) out-specifies it — _settings-shell.scss's
  // `.gl-sidebar.gl-sidebar--mode-main { display: flex !important }` (0,3,1,
  // desktop media query) keeps the primary rail visible and clickable beside
  // the settings rail. On mobile the drawer swaps rails, so the gear isn't
  // reachable in settings mode there — the Close pill owns closing on mobile.
  toggleSettings(event) {
    event.preventDefault()
    const next = this.activeValue === "settings" ? "main" : "settings"
    if (next === "main" && this.onSettingsSurface()) {
      persistSidebarMode("main")
      this.visitMain()
      return
    }
    if (next === "settings" && !this.pageReleasesSettingsRail()) return
    this.applyMode(next)
  }

  // Cancelable ask (see BEFORE_OPEN_SETTINGS_EVENT). dispatchEvent returns
  // false when a listener called preventDefault — i.e. the page has claimed
  // the request and will fire OPEN_SETTINGS_EVENT itself once it has made room.
  pageReleasesSettingsRail() {
    return document.dispatchEvent(new CustomEvent(BEFORE_OPEN_SETTINGS_EVENT, { cancelable: true }))
  }

  // The page finishing a claimed request (OPEN_SETTINGS_EVENT).
  openSettings() {
    this.applyMode("settings")
  }

  applyMode(mode) {
    this.activeValue = mode
    persistSidebarMode(mode)
    this.syncOpenMobileSidebar()
  }

  // Same swap as toggling Settings closed: the "Close" pill swaps the left nav
  // bar from the settings nav back to the default nav and does nothing else —
  // on a NON-settings page (the gear-opened rail over Dashboard etc.), where it
  // must NOT navigate. On a settings surface the swap alone would strand the
  // page (see onSettingsSurface), so there Close persists "main" and lets the
  // anchor's href (dashboard) navigate — the same place its no-JS fallback goes.
  showMain(event) {
    persistSidebarMode("main")
    if (this.onSettingsSurface()) return
    event.preventDefault()
    this.activeValue = "main"
    this.syncOpenMobileSidebar()
  }

  visitMain() {
    if (window.Turbo) {
      window.Turbo.visit(this.mainUrlValue)
    } else {
      window.location.assign(this.mainUrlValue)
    }
  }

  // The Wave 1 Settings shell keeps the primary rail visible (and clickable)
  // during settings mode (#10914). gl_sidebar_mode is a cookie decoupled from
  // the URL, so without this a primary-rail navigation would land on a main
  // page whose server render still says "settings" — dual rail + hero accent
  // on Leads, Dashboard, etc. A rail navigation is an explicit exit from
  // Settings: reset the cookie BEFORE Turbo issues the visit so the next
  // render derives "main". activeValue is deliberately left alone — flipping
  // it here would collapse the settings rail and reflow the page in the beat
  // before navigation completes.
  //
  // Delegated on BODY (the controller element), not the aside:
  // sidebar_group_controller re-parents every flyout submenu
  // (.gl-sidebar-group-items) to document.body, so an aside-scoped action
  // would never see clicks on admin/agency submenu links. The containment
  // check below owns the "primary-rail navigation" definition instead.
  exitSettingsOnMainNav(event) {
    if (this.activeValue !== "settings") return
    if (event.defaultPrevented) return
    // Mirror Turbo's click gate: only a plain left-click navigates the current
    // tab. Modified/middle clicks open a new tab and must not swap the nav bar
    // out from under the page the user is still on.
    if (typeof event.button === "number" && event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    // Primary rail or its re-parented flyouts only — never the settings rail
    // or page content. sidebar_group_controller moves .gl-sidebar-group-items
    // (admin/agency flyout submenus) to document.body on connect. The account
    // menu (a gl_more_menu in the rail) needs no entry: a pick closes it and
    // puts its list back in the rail before the click reaches this handler.
    if (!event.target.closest(".gl-sidebar--mode-main, .gl-sidebar-group-items")) return

    const link = event.target.closest("a[href]")
    if (!link) return
    // The ⚙ Settings gear sits on the primary rail but is wired to
    // sidebar-mode#toggleSettings (preventDefault, no navigation) — leave any
    // sidebar-mode-wired anchor alone.
    if ((link.dataset.action || "").includes("sidebar-mode#")) return

    persistSidebarMode("main")
  }

  syncOpenMobileSidebar() {
    if (!document.body.classList.contains("sidebar-open")) return

    const activeSidebar = document.querySelector(
      `.gl-sidebar.main-sidebar.gl-sidebar--mode-${this.activeValue}`
    )
    if (!activeSidebar) return

    // The revealed sidebar may still carry the desktop collapse stamp
    // (applySidebarState marks BOTH asides for any viewport ≤ 1200px, and
    // mobile_sidebar#open only strips the aside active at hamburger time).
    // Stripping here mirrors open(): without it the stamp's off-canvas
    // transform out-orders `.show` and the swapped-in rail never appears —
    // the "first Settings tap shows only the overlay" bug.
    activeSidebar.classList.remove(COLLAPSED_CLASS, FOOTER_HIDDEN_CLASS)

    document.querySelectorAll(".gl-sidebar.main-sidebar").forEach((sidebar) => {
      sidebar.classList.toggle("show", sidebar === activeSidebar)
    })

    // Mobile swaps the nav views via show/hide, which preserves each view's
    // scrollTop. Reset the newly revealed view to the top so "Close" (and
    // opening Settings) always starts from the top instead of wherever the user
    // had previously scrolled. Desktop scroll preservation lives in
    // sidebar_scroll_controller and is unaffected — this only runs while the
    // off-canvas drawer is open (sidebar-open), which is a mobile-only state.
    const scrollContainer = activeSidebar.querySelector(".gl-sidebar-scroll")
    if (scrollContainer) scrollContainer.scrollTop = 0
  }
}
